import { navStateStore } from '../../state/nav-state-store';
import { NaviError, NaviErrorTypes } from '../../utility/navi-error';
import { IAbTestingService } from './ab-testing-service.interface';
import {
  AvailableExperiment,
  Experiment,
  experimentsDefinitions,
  MatomoClient,
  Variation
} from '../../utility/constants/ab-testing';
import Logger from '../logger';
import { getHTMLElement } from '../../utility/helper';
import featureToggles from '../feature-toggles';
import { TMenu, VariationDefinition } from '../../entities/content';
import { navContentStore } from '../../state/nav-content-store';

export class MatomoAbTestingService implements IAbTestingService {
  client: MatomoClient | null = null;
  experiments: Experiment[] = [];

  async init(): Promise<void> {
    if (featureToggles.isABTestingToggleActive()) {
      try {
        await this.setClient();
        await this.loadExperiments();
      } catch (e) {
        Logger.error('Could not initialize AbTesting', e as Error);
      }
    }
  }

  execute(experimentName: AvailableExperiment): void {
    if (featureToggles.isABTestingToggleActive()) {
      try {
        const experiment = this.getExperiment(experimentName);
        const variation = this.getVariation(experiment);
        variation.activate();
      } catch (e) {
        Logger.error(`Could not execute experiment ${experimentName}`, e as Error);
      }
    }
  }

  executeContentChange(menu: TMenu): TMenu[] {
    if (featureToggles.isABTestingToggleActive()) {
      try {
        const experiment = this.getExperiment(menu.experienceName);
        const variation = this.getVariation(experiment);
        if (variation.name === 'original') {
          return menu.children;
        }
        return variation.activate() as unknown as TMenu[];
      } catch (e) {
        Logger.error(`Could not execute experiment ${menu.experienceName}`, e as Error);
      }
    }
    return menu.children;
  }

  private async setClient(): Promise<MatomoClient> {
    if (this.client) return Promise.resolve(this.client);

    await this.injectScript();
    const w = window as any;
    this.client = w.Matomo as MatomoClient;
    return this.client;
  }

  private async loadExperiments(): Promise<void> {
    const client = this.getClient();
    const ExperimentCreator = client.AbTesting.Experiment;
    const experimentsDefinitions = this.getAbTestingDefinitions();
    for (const experimentKey in experimentsDefinitions) {
      const experimentDefinition = experimentsDefinitions[experimentKey];
      const experiment = new ExperimentCreator(experimentDefinition);
      this.experiments.push(experiment);
    }
  }

  async loadContentGeneratorExperiments(): Promise<void> {
    if (featureToggles.isABTestingToggleActive()) {
      const client = this.getClient();
      const ExperimentCreator = client.AbTesting.Experiment;
      const contentDefinitions = navContentStore.state.abExperiences;

      if (contentDefinitions) {
        for (const experimentDefinition of contentDefinitions) {
          const variationDefinition: Variation[] = experimentDefinition.variations.map(
            (variation: VariationDefinition) => ({
              name: variation.name,
              activate: () => variation.children
            })
          );
          variationDefinition.push({
            name: 'original',
            activate: () => null
          });
          const experiment = new ExperimentCreator({ ...experimentDefinition, variations: variationDefinition });
          this.experiments.push(experiment);
        }
      }
    }
  }

  private getExperiment(name: AvailableExperiment): Experiment {
    const experiment = this.experiments.find((experiment) => experiment.name === name);
    if (!experiment) throw new NaviError(`No experiment with name: ${name} found`, NaviErrorTypes.GENERAL);
    return experiment;
  }

  private getVariation(experiment: Experiment): Variation {
    const variationName = experiment.getActivatedVariationName();
    const variation = experiment.variations.find((variation) => variation.name === variationName);

    if (!variation)
      throw new NaviError(
        `No variation in experiment: ${experiment.name} with name: ${variationName} found`,
        NaviErrorTypes.GENERAL
      );
    return variation;
  }

  private getClient(): MatomoClient {
    if (!this.client) throw new NaviError('No client is set, you must call init first', NaviErrorTypes.GENERAL);
    return this.client;
  }

  private injectScript(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.text = this.getMatomoScript();
      document.head.appendChild(s);

      const matomoScript = getHTMLElement(
        document.querySelector('script[src="//cdn.matomo.cloud/porsche.matomo.cloud/matomo.js"]')
      );

      if (matomoScript) {
        matomoScript.onload = () => {
          resolve(true);
        };
        matomoScript.onerror = () => reject(new NaviError('Could not inject Matomo script', NaviErrorTypes.GENERAL));
      } else {
        reject(new NaviError('There is no HTML Script element available', NaviErrorTypes.GENERAL));
      }
    });
  }

  private getAbTestingDefinitions() {
    return experimentsDefinitions;
  }

  private getMatomoScript(): string {
    const currentEnvironment = navStateStore.state.env;
    return `var _paq = window._paq = window._paq || [];
    _paq.push(['trackPageView']);
    _paq.push(['enableLinkTracking']);
    (function() {
      var u="https://porsche.matomo.cloud/";
      _paq.push(['setTrackerUrl', u+'matomo.php']);
      _paq.push(['setSiteId', ${currentEnvironment === 'production' ? '7' : '8'}]);
      var d=document, g=d.createElement('script'), s=d.getElementsByTagName('script')[0];
      g.async=true; g.src='//cdn.matomo.cloud/porsche.matomo.cloud/matomo.js'; s.parentNode.insertBefore(g,s);
    })
   ();`;
  }
}

export const MatomoAbTesting = new MatomoAbTestingService();
